import {makeObservable, observable, action, computed, runInAction} from "mobx";
import {notification, notificationId} from "../model/index.js";
import app from "../app.js";
import {loadObject} from "../utils/index.js";
import {Sync, outSyncHandler} from "../sync/index.js";
import {Store} from "./index.js";
import {fetchLockOptions} from "./common.js";
import AsyncLock from "async-lock";

const DEFAULT_LIMIT = 20;
const DEFAULT_INCLUDE_DISMISSED = false;

export class NotificationStore implements Store {
	_notifications: Record<notificationId, notification> = {};
	_fetchLock = new AsyncLock(fetchLockOptions);

	// Keep track of the params used last to load notifications from the backend.
	// This is useful to figure out whether we need to load again.
	_loaded = false;
	_loadedWithLimit = 0;
	_loadedIncludingDismissed = false;

	constructor() {
		makeObservable(this, {
			all:                       computed,
			_notifications:            observable,
			_loadedWithLimit:          observable,
			_loadedIncludingDismissed: observable,
			_clear:                    action,
			_load:                     action,
			_loadMultiple:             action,
			_remove:                   action
		});
	}

	_clear() {
		this._notifications = {};
		this._loaded = false;
		this._loadedWithLimit = 0;
		this._loadedIncludingDismissed = false;
	}

	_refresh() {
		if (!this._loaded)
			return;
		const limit = Math.max(DEFAULT_LIMIT, this._loadedWithLimit);
		const includeDismissed = this._loaded ? this._loadedIncludingDismissed : DEFAULT_INCLUDE_DISMISSED;
		// noinspection JSIgnoredPromiseFromCall
		this._fetchAll(limit, includeDismissed, true);
	}

	/**
	 * Returns a list of notifications sorted by event_dt (most recent first).
	 * Fetches notifications from the backend automatically.
	 */
	get all(): notification[] {
		// noinspection JSIgnoredPromiseFromCall
		this._fetchAll(DEFAULT_LIMIT, DEFAULT_INCLUDE_DISMISSED);

		return Object.values(this._notifications)
			.sort((a, b) => new Date(b.event_dt).valueOf() - new Date(a.event_dt).valueOf());
	}

	async _fetchAll(limit: number, includeDismissed: boolean, force = false) {
		await this._fetchLock.acquire("_fetchAll", async () => {
			if (!app.isLoggedIn)
				return;

			const shouldLoad = !this._loaded
				|| limit > this._loadedWithLimit
				|| (includeDismissed !== this._loadedIncludingDismissed);

			if (force || shouldLoad) {
				const notifications = await app.backend.getNotifications(limit, includeDismissed);

				runInAction(() => {
					if (force)
						this._clear();

					this._loadMultiple(notifications, limit, includeDismissed);
				});
			}
		});
	}

	_load(notification: notification) {
		loadObject(this._notifications, notification.id, notification);
	}

	_loadMultiple(notifications: notification[], limit: number, includeDismissed: boolean) {
		notifications.map(this._load.bind(this));
		this._loaded = true;
		this._loadedWithLimit = limit;
		this._loadedIncludingDismissed = includeDismissed;
	}

	_remove(id: notificationId) {
		delete this._notifications[id];

		// When removing a notification, we may need to load more from the backend.
		// This is how we keep track of that (see `this.fetchAll()`).
		this._loadedWithLimit--;
	}

	_setupSyncHandlers(sync: Sync) {
		// sync.setHandler(syncHandler.NOTIFICATION_ADD, () => {});
		// sync.setHandler(syncHandler.NOTIFICATION_MODIFY, () => {});
		// sync.setHandler(syncHandler.NOTIFICATION_DELETE, () => {});
	}

	_setupOutSyncHandlers(handler: outSyncHandler) {

	}
}

export default new NotificationStore();
